import React, { useEffect, useRef, useState } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { api } from '../lib/api';
import { MapPin } from 'lucide-react';

// Vertical-aware place autocomplete. Unlike AirportAutocomplete it returns typed places
// (City / Locality / Landmark / Region / Country / Property / Attraction) and NEVER airports
// for hotel/experience/tour. `onChange` receives the full place ({ name, code, type, lat... }).
export function LocationSearch({ vertical = 'hotel', label = 'Destination', placeholder = 'Search destination…', value, onChange, testid, icon: Icon = MapPin }) {
  const [q, setQ] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value?.name || ''); }, [value?.name]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try { const { data } = await api.locationsSearch(q, vertical); if (alive) { setResults(data.results || []); setHi(0); } }
      catch { if (alive) setResults([]); }
    }, 180);
    return () => { alive = false; clearTimeout(t); };
  }, [q, vertical]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = (p) => { onChange?.(p); setQ(p.name); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      {label && <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>}
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />}
        <Input value={q} placeholder={placeholder} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHi((i) => Math.min(i + 1, results.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((i) => Math.max(i - 1, 0)); }
            else if (e.key === 'Enter' && results[hi]) { e.preventDefault(); pick(results[hi]); }
          }}
          className="h-12 pl-9" data-testid={testid} autoComplete="off" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-border bg-popover shadow-lg overflow-hidden max-h-72 overflow-y-auto" data-testid={`${testid}-results`}>
          {results.map((p, i) => (
            <button key={p.slug} type="button" onMouseEnter={() => setHi(i)} onClick={() => pick(p)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2.5 ${i === hi ? 'bg-secondary' : ''}`}
              data-testid={`loc-opt-${p.slug}`}>
              <MapPin className="h-4 w-4 text-accent shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{p.name}</span>
                <span className="block text-[11px] text-muted-foreground truncate">{p.sub}</span>
              </span>
              <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">{p.type_label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
